import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spin, Skeleton, message, Button } from "antd";
import { ArrowUpOutlined } from "@ant-design/icons";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../api/api_instance";
import QuestionUtils from "../../../Organization/components/ViewForm/components/QuestionUtils";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";
import { API_URLS } from "../../../../api/apiUrls";

const EmpUserAssignFormDetail = ({ standalone = true, formId: propFormId }) => {
  const navigate = useNavigate();

  const location = useLocation();
  const { formId: allotmentId } = useParams();
  const {
    formTitle,
    formName,
    templateId,
    parentAllotmentId,
    formId: stateFormId,
    formIdRaw, // From ManageForms
    formAllotmentId, // From ManageForms
  } = location.state || {};

  // Resolve IDs based on source page
  const getId = (val) => (typeof val === "object" ? val?._id : val);

  // Template ID for sub-sections and questions
  const resolvedTemplateId =
    getId(templateId) ||
    getId(propFormId) ||
    getId(stateFormId) ||
    getId(formIdRaw);

  // Parent Allotment ID for sections
  const resolvedParentAllotmentId =
    getId(parentAllotmentId) || getId(formAllotmentId);

  // For other API compatibility
  const resolvedFormId =
    getId(propFormId) || getId(stateFormId) || resolvedTemplateId;

  // Main Allotment ID for fetching answers
  const mainAllotmentId =
    getId(allotmentId) ||
    getId(parentAllotmentId) ||
    getId(formAllotmentId) ||
    getId(stateFormId);

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [flatAnswers, setFlatAnswers] = useState({}); // Flat map for UI
  const [searchTerm, setSearchTerm] = useState("");
  const [expandAll, setExpandAll] = useState(true); // Default to true for better visibility
  const [showGoTop, setShowGoTop] = useState(false);

  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  // Fetch answers for the form
  const fetchAnswers = async (id) => {
    // Prioritize standardFormAllotmentId from state for the get-answer API
    const allotmentIdToUse =
      getId(location.state?.standardFormAllotmentId) || id || mainAllotmentId;
    if (!allotmentIdToUse) return [];

    try {
      // Use the correct API constant from apiUrls.js
      const apiUrl = API_URLS.GET_QUESTION_ANSWERS(allotmentIdToUse);
      const response = await getApi({ apiUrl });

      if (response.success && response.data) {
        let combinedMap = {};
        const responseData = Array.isArray(response.data)
          ? response.data
          : [response.data];

        responseData.forEach((item) => {
          if (item.answers && Array.isArray(item.answers)) {
            // Use a local helper to flatten each item and merge
            const itemMap = {};
            const localFlatten = (list, prefix = "") => {
              if (!list || !Array.isArray(list)) return;
              list.forEach((ans) => {
                const bId = ans.questionId || ans.questionID;
                if (!bId) return;
                console.log(
                  `Processing Answer: ${bId}, Type: ${ans.questionType}`,
                );
                let mId = bId;
                if (prefix) {
                  if (bId.includes(":::")) {
                    const [iP, oP] = bId.split(":::");
                    mId = `${iP}${prefix}:::${oP}`;
                  } else {
                    mId = `${bId}${prefix}`;
                  }
                }

                if (ans.questionType === "table") {
                  // Aggressively extract all fields from all rows into suffixed keys for pre-selection
                  if (ans.tableRows && Array.isArray(ans.tableRows)) {
                    ans.tableRows.forEach((row) => {
                      if (row.values) {
                        Object.entries(row.values).forEach(
                          ([fieldId, fieldVal]) => {
                            const prefixedKey = `${mId}_${fieldId}`;
                            [prefixedKey, fieldId].forEach((k) => {
                              if (itemMap[k] === undefined) {
                                itemMap[k] = fieldVal;
                              } else {
                                const current = Array.isArray(itemMap[k])
                                  ? itemMap[k]
                                  : [itemMap[k]];
                                const next = Array.isArray(fieldVal)
                                  ? fieldVal
                                  : [fieldVal];
                                itemMap[k] = Array.from(
                                  new Set([...current, ...next]),
                                );
                              }
                            });
                          },
                        );
                      }
                    });
                  }

                  // Standard table data mapping (categorized)
                  const tableData = {};
                  ans.tableRows?.forEach((row) => {
                    const itemType = Array.isArray(row.values?.item_type)
                      ? row.values.item_type[0]
                      : "default";
                    if (!tableData[itemType]) tableData[itemType] = [];
                    tableData[itemType].push(row);
                  });
                  itemMap[mId] = tableData;
                } else if (
                  (ans.value &&
                    typeof ans.value === "object" &&
                    ans.value.details) ||
                  (ans.details && Array.isArray(ans.details))
                ) {
                  const details = ans.value?.details || ans.details;
                  const tripCount =
                    ans.value?.number_of_trip !== undefined
                      ? ans.value.number_of_trip
                      : ans.number_of_trip !== undefined
                        ? ans.number_of_trip
                        : ans.value !== undefined
                          ? ans.value
                          : 0;

                  // Set the top-level question value (the trip count)
                  itemMap[mId] = tripCount;

                  details.forEach((d, idx) => {
                    Object.keys(d).forEach((k) => {
                      if (Array.isArray(d[k])) {
                        const suffix = `_trip_${idx}`;
                        localFlatten(d[k], suffix);
                      }
                    });
                  });
                } else {
                  // Handle array merging for multiselect/repeater values to prevent overwriting
                  if (ans.value !== undefined || ans.answer !== undefined) {
                    const newVal =
                      ans.value !== undefined ? ans.value : ans.answer;
                    const currentVal = itemMap[mId];

                    if (currentVal === undefined) {
                      itemMap[mId] = newVal;
                    } else {
                      // Merge both into an array if they aren't already
                      const currentArray = Array.isArray(currentVal)
                        ? currentVal
                        : [currentVal];
                      const newArray = Array.isArray(newVal)
                        ? newVal
                        : [newVal];
                      itemMap[mId] = Array.from(
                        new Set([...currentArray, ...newArray]),
                      );
                    }
                  } else {
                    itemMap[mId] =
                      itemMap[mId] !== undefined ? itemMap[mId] : "";
                  }
                }
              });
            };
            localFlatten(item.answers);
            console.log("EmpUserAssignFormDetail - ItemMap for item:", itemMap);
            Object.keys(itemMap).forEach((key) => {
              if (combinedMap[key] === undefined) {
                combinedMap[key] = itemMap[key];
              } else {
                if (
                  typeof itemMap[key] !== "object" ||
                  Array.isArray(itemMap[key])
                ) {
                  const current = Array.isArray(combinedMap[key])
                    ? combinedMap[key]
                    : [combinedMap[key]];
                  const next = Array.isArray(itemMap[key])
                    ? itemMap[key]
                    : [itemMap[key]];
                  combinedMap[key] = Array.from(new Set([...current, ...next]));
                } else {
                  combinedMap[key] = itemMap[key];
                }
              }
            });
          }
        });

        console.log(
          "EmpUserAssignFormDetail - Final Combined Map Keys:",
          Object.keys(combinedMap),
        );
        setFlatAnswers(combinedMap);
        setAnswers(combinedMap);
        return responseData;
      }
    } catch (error) {
      console.error("Error fetching answers", error);
    }
    return [];
  };

  // Fetch sections
  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi(
        { apiUrl: API_URLS.GET_HIERARCHY_SECTION(mainAllotmentId) },
        true,
      );

      if (response.success) {
        if (response.data.sections.length > 0) {
          const firstSection = response.data.sections[0];
          setSections(response.data.sections);
          setSelectedSection(firstSection.sectionId);

          // FETCH ANSWERS FIRST
          const currentFlatAnswers = await fetchAnswers(allotmentId);
          await fetchSubsections(firstSection.sectionId, currentFlatAnswers);
        }
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subsections
  const fetchSubsections = async (sectionId, currentAnswers) => {
    const idToUse = resolvedTemplateId;
    if (!idToUse || !sectionId) return;
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_HIERARCHY_SUB_SECTION(
          resolvedTemplateId,
          sectionId,
        ),
      });

      if (response.success) {
        setSubsections(response.data.subsections);
        setSelectedSection(sectionId);

        if (response.data.subsections.length > 0) {
          const firstSubsection = response.data.subsections[0];
          setSelectedSubsection(firstSubsection.subsectionId);
          await fetchQuestions(
            sectionId,
            firstSubsection.subsectionId,
            currentAnswers,
          );
        } else {
          setQuestions([]);
        }
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (sectionId, subsectionId, currentAnswers) => {
    const section = sectionId || selectedSection;
    const subsection = subsectionId || selectedSubsection;
    const idToUse = resolvedTemplateId;
    if (!section || !subsection || !idToUse) return;

    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_EMPLOYEE_STANDARD_QUESTIONS_FROM_SUBSECTION(
          section,
          idToUse,
          subsection,
        ),
      });

      if (response.success) {
        let divisions = response.data.divisions || [];

        // Use the flatAnswers we just fetched
        // We use a local map here to ensure we have the latest data before state updates
        const localMap = {};
        const buildLocalMap = (ansList) => {
          if (!ansList || !Array.isArray(ansList)) return;
          ansList.forEach((a) => {
            const qid = a.questionId || a.questionID;
            if (!qid) return;
            localMap[qid] = a;

            // Aggressively extract all fields from all rows into localMap for enhancement
            if (a.questionType === "table" && a.tableRows?.length > 0) {
              a.tableRows.forEach((row) => {
                if (row.values) {
                  Object.keys(row.values).forEach((k) => {
                    const fieldVal = row.values[k];
                    // Map to both global and potentially prefixed keys in localMap
                    if (!localMap[k]) {
                      localMap[k] = { questionId: k, value: fieldVal };
                    }
                  });
                }
              });

              // Bidirectional mapping for Scope 3 tables
              if (qid === "scope3_categories_capital_goods_table") {
                localMap["scope3_categories_non_capital_goods_table"] = a;
              } else if (qid === "scope3_categories_non_capital_goods_table") {
                localMap["scope3_categories_capital_goods_table"] = a;
              }
            }

            // Recursive extraction for trips
            const value = a.value || a.answer;
            if (value && typeof value === "object" && value.details) {
              value.details.forEach((d) => {
                Object.values(d).forEach((v) => {
                  if (Array.isArray(v)) buildLocalMap(v);
                });
              });
            } else if (a.details && Array.isArray(a.details)) {
              a.details.forEach((d) => {
                Object.values(d).forEach((v) => {
                  if (Array.isArray(v)) buildLocalMap(v);
                });
              });
            }
          });
        };

        if (currentAnswers) {
          currentAnswers.forEach((sub) => {
            buildLocalMap(sub.answers);
          });
        }

        const enhancedDivisions = divisions.map((div) => ({
          ...div,
          questions: (div.questions || []).map((q) => {
            const qId = (q.questionId || q.questionID || "").trim();
            const savedAns = localMap[qId];
            if (savedAns) {
              const isTable =
                q.questionType === "table" || q.fieldType === "table";
              const tableData = savedAns.tableRows || [];

              // Determine the actual value to inject
              let regularVal;

              // Check for nested trip count in value object (Employee/New format)
              if (
                savedAns.value &&
                typeof savedAns.value === "object" &&
                savedAns.value.number_of_trip !== undefined
              ) {
                regularVal = savedAns.value.number_of_trip;
              }
              // Check for trip count in details array (Old format)
              else if (savedAns.details && Array.isArray(savedAns.details)) {
                regularVal = savedAns.number_of_trip || savedAns.value || 0;
              }
              // Standard values
              else {
                regularVal =
                  savedAns.value !== undefined
                    ? savedAns.value
                    : savedAns.answer !== undefined
                      ? savedAns.answer
                      : "";
              }

              return {
                ...q,
                value: isTable ? { default: tableData } : regularVal,
                answer: isTable ? { default: tableData } : regularVal,
                tableRows: tableData,
              };
            }
            return q;
          }),
        }));

        setQuestions(enhancedDivisions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions", error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();

    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [allotmentId, templateId]);

  const handleSubsectionClick = async (subsectionId) => {
    setSelectedSubsection(subsectionId);
    // Refresh answers just in case before fetching questions for the new subsection
    const currentFlatAnswers = await fetchAnswers(allotmentId);
    await fetchQuestions(selectedSection, subsectionId, currentFlatAnswers);
  };

  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]);

  const scrollToTop = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowGoTop(false);
  };

  return (
    <div
      className={
        standalone
          ? "min-h-screen bg-[#F9FBFC] p-6 md:p-8 space-y-8 animate-fadeIn"
          : "space-y-6"
      }
    >
      {standalone && (
        <>
          <div className="flex justify-end text-xs font-light h-4 ">
            <p>Home</p>
            <span>/</span>
            <TextStyles className="text-[#06513D]">Manage Form</TextStyles>
            <span>/</span>
            <TextStyles className="text-[#06513D]">View Answers</TextStyles>
          </div>
          <div className="flex justify-between h-12 pb-2">
            <div className="flex items-center">
              <Back className="cursor-pointer " onClick={() => navigate(-1)} />
              <h3 className="text-[#06513D] mt-1 text-xl">
                {formName || "Form Details"}
              </h3>
            </div>
          </div>
        </>
      )}

      <div className="w-full bg-[#fff] rounded-t-[10px] shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.map((section, index) => (
            <div
              key={index}
              className={`relative h-10 px-2 flex items-center justify-center text-[13px] font-medium cursor-pointer rounded-xl mx-1 my-1 transition-all duration-200 ease-in-out border border-[#3BA2B959] ${
                selectedSection === section.sectionId
                  ? "bg-[#06513D] text-white shadow-md transform scale-105"
                  : "bg-transparent text-gray-700 hover:text-gray-900"
              }`}
              onClick={() => fetchSubsections(section.sectionId)}
            >
              <span className="text-center leading-tight">{section.name}</span>
            </div>
          ))}
        </div>

        {selectedSection && (
          <div className="flex flex-wrap gap-5 p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
            {subsections.map((sub) => (
              <div
                key={sub.subsectionId}
                className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 ${
                  selectedSubsection === sub.subsectionId
                    ? "shadow-md bg-gray-100"
                    : "bg-transparent text-black"
                }`}
                onClick={() => handleSubsectionClick(sub.subsectionId)}
              >
                {sub.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#fff] w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6 relative">
        <div className="mt-4 ml-3">
          <div className="flex flex-col md:flex-row items-center justify-between w-[90%] m-auto gap-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-sm font-light w-72">
                TOTAL QUESTIONS: {questions?.length || 0}
              </p>
              <SearchBar
                width="100%"
                onSearch={(value) => setSearchTerm(value)}
                className="sm:w-[250px] md:w-[300px]"
              />
            </div>
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-6 py-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer} ref={scrollableContainerRef}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : selectedSubsection ? (
              <div className="mx-20 shadow-[0_0_16px_0_#3BA2B91F]">
                <QuestionUtils
                  isEmployee={true}
                  questions={questions}
                  expandAll={expandAll}
                  answers={flatAnswers}
                  setAnswers={setFlatAnswers}
                />
              </div>
            ) : (
              <p className="text-gray-500 text-center p-8">
                Please select a subsection to view questions.
              </p>
            )}

            {showGoTop && questions.length > 0 && (
              <Button
                type="primary"
                shape="circle"
                icon={<ArrowUpOutlined />}
                size="large"
                onClick={scrollToTop}
                style={{
                  position: "absolute",
                  bottom: "30px",
                  right: "60px",
                  background: "#06513D",
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmpUserAssignFormDetail;
